<template>
    <div class="c-calendar" aria-live="polite" :aria-busy="isLoading">
        <CalendarToolbar
            :title="currentTitle"
            @prev="handlePrev"
            @next="handleNext"
            :event-types="eventTypes"
            :disable-prev="disablePrevButton"
        />

        <div class="c-loader" v-if="isLoading">
            <span class="c-loader__spinner" aria-hidden="true"></span>
            <span class="c-loader__text">Loading...</span>
        </div>
        <div class="c-loader" v-if="errorMsg">
            <span class="c-loader__text">{{ errorMsg }}</span>
        </div>
        
        <FullCalendar ref="fullCalendar" :options="calendarOptions" />
    </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
//Custom header for the calendar as a component
import CalendarToolbar from "./CalendarToolbar.vue";

import momentPlugin from "@fullcalendar/moment";
import moment from "moment";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";

const venues = window.calendar_venues.venues;
const mountedEventInCalendar = debounceProcessFavourites();

export default {
    name: "Calendar",
    props: ["firstInstance", "lastInstance"],
    components: {
        FullCalendar,
        CalendarToolbar,
    },
    data() {
        return {
            calendarOptions: {
                resourceAreaHeaderContent: "Venue",
                firstDay: 0,
                headerToolbar: false,
                footerToolbar: false,
                dayMaxEventRows: true,
                views: {
                    resourceTimeline: {
                        titleFormat: {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric"
                        }
                    }
                },
                height: "auto",
                plugins: [momentPlugin, resourceTimelinePlugin],
                initialView: "resourceTimeline",
                events: [],
                displayEventTime: false,
                eventContent: this.eventContent,
                fixedWeekCount: false,
                initialDate: this.firstInstance,
                validRange: this.validRange,
                slotDuration: "00:15:00",
                resourceAreaWidth: "20%",
                resources: [],
                eventClick(info) {
                    info.jsEvent.preventDefault();
                },
                eventDidMount(info) {
                    const el = info.el;

                    // Remove empty <a> tag logic - full calendar really insists on dropping them in :/
                    if (
                        el.tagName === "A" &&
                        el.getAttribute("href") === "null" &&
                        !el.hasChildNodes() &&
                        el.textContent.trim() === ""
                    ) {
                        el.remove();
                        return;
                    }

                    // Replace <a> with <div> for a11y
                    if (el.tagName === "A") {
                        const div = document.createElement("div");
                        div.className = el.className;
                        div.style.cssText = el.style.cssText;
                        while (el.firstChild) div.appendChild(el.firstChild);
                        if (el.parentNode) el.parentNode.replaceChild(div, el);
                        info.el = div;
                    }

                    // Attach toggle listener for the info boxes
                    const toggleBtn = info.el.querySelector(".c-calendar-instance__toggle");
                    const infoPanel = info.el.querySelector(".c-calendar-instance__info");
                    const instance = toggleBtn?.closest('.c-calendar-instance');
                    const wasOpen = infoPanel?.classList.contains('is-open');

                    if (toggleBtn && infoPanel) {
                        toggleBtn.addEventListener("click", (e) => {
                            e.stopPropagation();

                            document.querySelectorAll('.c-calendar-instance__info.is-open').forEach(p => {
                                // Close all others
                                p.classList.remove('is-open');
                                // remove class on tr to give info panel overlay not underlay
                                const lane = p.closest('.fc-timeline-lane-frame');
                                lane?.classList.remove('is-elevated');

                                const harness = p.closest('.fc-timeline-event-harness');
                                harness?.classList.remove('is-expanded');

                                // Reset all toggle buttons' aria-expanded
                                const otherToggle = p.closest('.c-calendar-instance')?.querySelector('.c-calendar-instance__toggle');
                                if (otherToggle) {
                                    otherToggle.setAttribute('aria-expanded', 'false');
                                }
                            });

                            if (!wasOpen) {
                                infoPanel.classList.add('is-open');
                                toggleBtn.setAttribute('aria-expanded', 'true');
                                // increas z index of tr so it overlays the info panel
                                const lane = instance.closest('.fc-timeline-lane-frame');
                                if (lane) lane.classList.add('is-elevated');

                                const harness = toggleBtn.closest('.fc-timeline-event-harness');
                                if (harness) harness.classList.add('is-expanded');

                                function handleClickOutside(e) {
                                    if (!infoPanel.contains(e.target) && !toggleBtn.contains(e.target)) {
                                        infoPanel.classList.remove('is-open');
                                        const lane = instance.closest('.fc-timeline-lane-frame');
                                        if (lane) lane.classList.remove('is-elevated');

                                        const harness = toggleBtn.closest('.fc-timeline-event-harness');
                                        if (harness) harness.classList.remove('is-expanded');

                                        document.removeEventListener('click', handleClickOutside);
                                    }
                                }

                                document.addEventListener('click', handleClickOutside);
                                // Panel toggle logics
                                requestAnimationFrame(() => {
                                    const toggleRect = toggleBtn.getBoundingClientRect();
                                    const container = document.querySelector('.fc-scrollgrid-section-body');
                                    const containerRect = container.getBoundingClientRect();

                                    infoPanel.style.visibility = 'hidden';
                                    infoPanel.style.display = 'block';
                                    infoPanel.style.maxHeight = '';
                                    infoPanel.style.overflow = '';

                                    const panelHeight = infoPanel.getBoundingClientRect().height;

                                    const gap = 4;
                                    const spaceBelow = containerRect.bottom - toggleRect.bottom - gap;
                                    const spaceAbove = toggleRect.top - containerRect.top - gap;

                                    infoPanel.style.top = "";
                                    infoPanel.style.bottom = "";
                                    infoPanel.style.visibility = '';
                                    infoPanel.style.display = '';

                                    //Display panel above or below depending on how near it is the the edge

                                    let showAbove = spaceBelow < panelHeight && spaceAbove > panelHeight;

                                    if (showAbove) {
                                        infoPanel.style.top = `-${Math.min(panelHeight, spaceAbove) + gap}px`;
                                        infoPanel.style.maxHeight = `${spaceAbove}px`;
                                    } else {
                                        const topOffset = toggleBtn.offsetHeight + gap;
                                        infoPanel.style.top = `${topOffset}px`;
                                        infoPanel.style.maxHeight = `${spaceBelow}px`;
                                    }

                                    infoPanel.style.overflow = 'auto';

                                    // scroll event into middle
                                    infoPanel.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'center',
                                        inline: 'center'
                                    });
                                });

                            }
                        });
                    }

                    mountedEventInCalendar?.(info);
                },
                schedulerLicenseKey: "0106752278-fcs-1753802454",
            },
            isLoading: true,
            calendarApi: null,
            errorMsg: "",
            eventTypes: [],
            currentTitle: "",
            emptyAnchorObserver: null,
            disablePrevButton: false,
        };
    },
    mounted() {
        this.calendarApi = this.$refs.fullCalendar.getApi();

        if (this.calendarApi) {
            this.calendarApi.on("datesSet", this.onDatesSet);
            const currentDate = moment.utc(this.calendarApi.getDate()).startOf("day");
            const today = moment.utc().startOf("day");
            this.disablePrevButton = currentDate.isSameOrBefore(today);
        }



        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("dates")) {
            const selectedDate = urlParams.get("dates");
            if (selectedDate) {
                const date = moment(selectedDate, "MMMM D YYYY");
                if (date) {
                    this.calendarApi.gotoDate(date.format("YYYY-MM-DD"));
                }
            }
        }

        this.updateTitle();
        this.fetchEvents();
        this.nextAndPreviousButtonAdditionalEvents();
        this.initEmptyAnchorObserver();

        //close panels on esc

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' || e.key === 'Esc') {
                document.querySelectorAll('.c-calendar-instance__info.is-open').forEach((panel) => {
                    panel.classList.remove('is-open');

                    // Remove elevated lane style
                    const lane = panel.closest('.fc-timeline-lane-frame');
                    if (lane) lane.classList.remove('is-elevated');

                    // Update aria-expanded on the associated toggle button
                    const instance = panel.closest('.c-calendar-instance');
                    const toggle = instance?.querySelector('.c-calendar-instance__toggle');
                    if (toggle) toggle.setAttribute('aria-expanded', 'false');
                });
            }
        });

    },
    beforeUnmount() {
        if (this.calendarApi) {
            this.calendarApi.off("datesSet", this.onDatesSet);
        }
        if (this.emptyAnchorObserver) {
            this.emptyAnchorObserver.disconnect();
            this.emptyAnchorObserver = null;
        }
    },
    methods: {
        // Full calendar insists on sticking empty a tags in even when you set the events to not be clickable and change the a tags to divs.. 
        // So added observer to strip out the empty a tags. 
        initEmptyAnchorObserver() {
            const calendarEl = this.$refs.fullCalendar?.$el;
            if (!calendarEl) return;

            this.emptyAnchorObserver = new MutationObserver(() => {
                const anchors = calendarEl.querySelectorAll("a.fc-event");
                anchors.forEach((anchor) => {
                    if (
                        (!anchor.hasChildNodes() || anchor.textContent.trim() === "") &&
                        (anchor.getAttribute("href") === "null" || anchor.getAttribute("href") === null)
                    ) {
                        anchor.remove();
                    }
                });
            });

            this.emptyAnchorObserver.observe(calendarEl, {
                childList: true,
                subtree: true,
            });
        },
        onDatesSet() {
            this.fetchEvents();
            const currentDate = moment.utc(this.calendarApi.getDate()).startOf('day');
            const today = moment.utc().startOf('day');
            this.disablePrevButton = currentDate.isSameOrBefore(today);
        },
        eventContent(arg) {
            const container = document.createElement("div");
            container.className = "c-calendar-item";
            container.innerHTML = arg.event.extendedProps.html || arg.event.title || "No content";
            return { domNodes: [container] };
        },
        fetchEvents() {
            this.isLoading = true;
            const url = new URL(wpApiSettings.root + "v1/attendable/calendar/instances");
            url.search = this.setQueryParams().toString();

            fetch(url, {
                method: "GET",
                headers: {
                    "X-WP-Nonce": wpApiSettings.nonce,
                },
            })
                .then((response) => {
                    if (!response.ok) throw new Error("Network response was not ok");
                    return response.json();
                })

                .then((instances) => {

                    const cleanedInstances = instances
                        .map(({ url, ...rest }) => ({
                            ...rest,
                            interactive: false,
                        }))
                        .filter((event) => event.title || (event.extendedProps && event.extendedProps.html));

                    this.calendarOptions = {
                        ...this.calendarOptions,
                        events: cleanedInstances,
                    };

                    this.setVenues();

                    const uniqueTypes = new Set(cleanedInstances.map((i) => i.eventType));
                    this.eventTypes = Array.from(uniqueTypes).sort();

                    // Work out start time
                    let start = '24:00';
                    cleanedInstances.forEach((instance) => {
                        const time = instance.start.split('T')[1] || '06:00';
                        if (time < start)   {
                            start = time;
                        }
                    });
                    if (start != '24:00')   {
                        const parts = start.split(':');
                        this.calendarApi.scrollToTime(`${parts[0]-1}:${parts[1]}`);
                    }
                    this.isLoading = false;
                })
                .catch((error) => {
                    this.isLoading = false;
                    console.error(error);
                    this.errorMsg = "Unable to load calendar events. Please try again later.";
                });
        },
        setVenues() {
            const allVenues = this.calendarOptions.events.map((instance) => instance.resourceId);

            // First, remove all resources
            this.calendarApi.getTopLevelResources().forEach((resource) => {
                resource.remove();
            });

            // Add actual venues
            venues.forEach((venue) => {
                if (allVenues.includes(venue.id)) {
                    this.calendarApi.addResource(venue);
                }
            });

            // Add bottom spacer
            this.calendarApi.addResource({
                id: 'z-spacer-bottom',
                title: '',
                eventColor: 'transparent',
                className: 'spacer-row'
            });
        },
        setQueryParams() {
            const urlParams = new URLSearchParams(window.location.search);
            const currentDate = moment.utc(this.calendarApi.currentData.currentDate);
            if (!urlParams.get("dates")) {
                urlParams.append("dates", moment.utc(currentDate).format("MMMM DD YYYY"));
            } else {
                urlParams.set("dates", moment.utc(currentDate).format("MMMM DD YYYY"));
            }
            return urlParams;
        },
        nextAndPreviousButtonAdditionalEvents() {
            const calendar = this.calendarApi;
            if (calendar) {
                calendar.on("datesSet", () => {
                    this.fetchEvents();
                });
            }
        },
        handlePrev() {
            this.calendarApi.prev();
            this.updateTitle();
            this.fetchEvents();
        },
        handleNext() {
            this.calendarApi.next();
            this.updateTitle();
            this.fetchEvents();
        },
        updateTitle() {
            this.currentTitle = this.calendarApi?.view?.title || "";
        },
    },
};
</script>
